import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import CategoryButton from "../components/CategoryButton";
import ProductCard from "../components/ProductCard";
import RequestCard from "../components/RequestCard";

import {
  getCategories,
  getProducts,
  getPromoCodes,
  getRequests,
} from "../utils/api";

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function normCat(c) {
  return {
    id: c?.id,
    label: c?.title ?? c?.label ?? "Category",
    title: c?.title ?? c?.label ?? "Category",
    image_path: c?.image_path ?? c?.image ?? "",
  };
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function prettyRegion(r) {
  const s = String(r || "");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

function maxRegionValue(map) {
  let bestRegion = "";
  let bestValue = null;
  if (!map || typeof map !== "object") return { region: "", value: null };

  for (const [region, value] of Object.entries(map)) {
    if (
      typeof value === "number" &&
      (bestValue === null || value > bestValue)
    ) {
      bestValue = value;
      bestRegion = region;
    }
  }
  return { region: bestRegion, value: bestValue };
}

function psiColor(v) {
  if (typeof v !== "number") return "#6b7280";
  return v <= 50 ? "#4caf50" : v <= 100 ? "#ff9800" : "#f44336";
}

function pm25Color(v) {
  if (typeof v !== "number") return "#6b7280";
  return v <= 12 ? "#4caf50" : v <= 55 ? "#ff9800" : "#f44336";
}

const SG_CARE_LINKS = [
  {
    title: "HealthHub: Healthy Ageing",
    desc: "Programmes & tips for ageing well (exercise, falls prevention, etc.)",
    href: "https://www.healthhub.sg/programmes/healthy-ageing",
    emoji: "🧓",
    img: "/health/healthy-ageing.png",
  },
  {
    title: "AIC: Care Services",
    desc: "Find community care, home care, day care, nursing home options",
    href: "https://www.aic.sg/care-services/",
    emoji: "🏠",
    img: "/health/aic.png",
  },
  {
    title: "CHAS Subsidies (MOH)",
    desc: "Subsidies at participating GP & dental clinics (incl. PG/MG)",
    href: "https://www.moh.gov.sg/managing-expenses/schemes-and-subsidies/chas/",
    emoji: "💳",
    img: "/health/chas.png",
  },
  {
    title: "CareShield Life (MOH)",
    desc: "Long-term care insurance support for severe disability needs",
    href: "https://www.moh.gov.sg/managing-expenses/schemes-and-subsidies/careshield-life/",
    emoji: "🛡️",
    img: "/health/careshield.png",
  },
  {
    title: "Active Ageing Centres",
    desc: "Find nearby AACs and community activities/support",
    href: "https://supportgowhere.life.gov.sg/services/SVC-AACAAACHASACS/active-ageing-centres-aac",
    emoji: "🤝",
    img: "/health/aac.png",
  },
  {
    title: "Community Nursing Post",
    desc: "Basic nursing help + assessments in the community",
    href: "https://www.healthhub.sg/support-and-tools/support-and-care-programmes/community-nurse-post",
    emoji: "🩺",
    img: "/health/cnp.png",
  },
];

export default function Home({ searchQuery, currentUser }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);

  const [buyerRequests, setBuyerRequests] = useState([]);
  const [healthStats, setHealthStats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const banners = [
    { src: "/banners/banner1.png", alt: "SecondAid banner 1" },
    { src: "/banners/banner2.png", alt: "SecondAid banner 2" },
    { src: "/banners/banner1.png", alt: "SecondAid banner 1" },
  ];
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % banners.length);
    }, 4500);
    return () => clearInterval(id);
  }, [banners.length]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");
        try {
          const data = await fetchJson(
            "https://api.data.gov.sg/v1/environment/psi",
          );
          if (cancelled) return;

          const item = data?.items?.[0];
          const updated = item?.update_timestamp || item?.timestamp || "";

          const psiBest = maxRegionValue(
            item?.readings?.psi_twenty_four_hourly,
          );
          const pm25Best = maxRegionValue(
            item?.readings?.pm25_twenty_four_hourly,
          );

          const metrics = [];

          if (typeof psiBest.value === "number") {
            metrics.push({
              label: "PSI (24h)",
              value: psiBest.value,
              meta: `Highest: ${prettyRegion(psiBest.region)}`,
              color: psiColor(psiBest.value),
            });
          }

          if (typeof pm25Best.value === "number") {
            metrics.push({
              label: "PM2.5 (24h, µg/m³)",
              value: pm25Best.value,
              meta: `Highest: ${prettyRegion(pm25Best.region)}`,
              color: pm25Color(pm25Best.value),
            });
          }

          // Only set if we got at least one metric
          if (metrics.length > 0) {
            setHealthStats({
              title: "🌿 Community Health Watch",
              subtitle:
                "Live air indicators + quick care access for the community.",
              updated,
              metrics,
            });
          }
        } catch (err) {
          console.warn("Health API failed", err);

        }

        const [cats, prods, promos, reqs] = await Promise.all([
          getCategories(),
          getProducts(),
          getPromoCodes().catch(() => []),
          getRequests("open").catch(() => []),
        ]);

        if (cancelled) return;

        setCategories(safeArray(cats).map(normCat));
        setProducts(safeArray(prods));
        setPromoCodes(safeArray(promos));
        setBuyerRequests(safeArray(reqs).slice(0, 4));
      } catch (e) {
        if (!cancelled) setError(String(e?.message || "Failed to fetch"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const q = String(searchQuery || "")
    .trim()
    .toLowerCase();
  const role = String(currentUser?.role || "").toLowerCase();
  const isSellerOrAdmin = role === "seller" || role === "admin";

  const visibleProducts = isSellerOrAdmin
    ? safeArray(products)
    : safeArray(products).filter(
        (p) => String(p?.status || "").toLowerCase() === "available",
      );

  const filteredProducts = !q
    ? visibleProducts
    : visibleProducts.filter((p) =>
        String(p?.name || "")
          .toLowerCase()
          .includes(q),
      );

  const featured = (filteredProducts || []).slice(0, 8);

  return (
    <div className="container pagePadBottom homePage">
      {/* CAROUSEL */}
      <section className="homeSection">
        <div className="sectionHead">
          <div>
            <div className="sectionTitle">Welcome to SecondAid</div>
            <div className="sectionSub">
              Buy, sell, and help the community - without the clutter.
              {q ? (
                <span className="subHint">
                  {" "}
                  Showing results for{" "}
                  <span className="strong">{searchQuery}</span>
                </span>
              ) : null}
            </div>
          </div>

          {promoCodes.length > 0 ? (
            <div className="promoRow">
              <span className="promoLabel">Promos:</span>
              {promoCodes.slice(0, 2).map((p, idx) => (
                <span key={idx} className="promoChip">
                  {p?.code ?? p?.promo_code ?? p?.name ?? "PROMO"}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="carousel">
          <div className="carouselFrame">
            <img
              className="carouselImg"
              src={banners[slide]?.src}
              alt={banners[slide]?.alt || "Banner"}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <button
              type="button"
              className="carouselBtn left"
              onClick={() =>
                setSlide((s) => (s - 1 + banners.length) % banners.length)
              }
              aria-label="Previous slide"
              title="Previous"
            >
              ‹
            </button>

            <button
              type="button"
              className="carouselBtn right"
              onClick={() => setSlide((s) => (s + 1) % banners.length)}
              aria-label="Next slide"
              title="Next"
            >
              ›
            </button>

            <div className="carouselDots">
              {banners.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={i === slide ? "dot dotActive" : "dot"}
                  onClick={() => setSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  title={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HEALTH STATS */}
      {healthStats && (
        <section className="homeSection" style={{ marginBottom: 20 }}>
          <div
            style={{
              padding: "20px 20px",
              background: "linear-gradient(to right, #f9fbfd, #ffffff)",
              borderRadius: "12px",
              border: "1px solid #e1e4e8",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 240 }}>
              <div className="sectionTitle" style={{ fontSize: "1.1rem" }}>
                {healthStats.title}
              </div>
              <div className="sectionSub">{healthStats.subtitle}</div>

              {healthStats.updated ? (
                <div
                  style={{ fontSize: "0.78rem", color: "#666", marginTop: 6 }}
                >
                  Updated: {new Date(healthStats.updated).toLocaleString()}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 18, alignItems: "flex-end" }}>
              {(healthStats.metrics || []).map((m) => (
                <div
                  key={m.label}
                  style={{ textAlign: "right", minWidth: 140 }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#888",
                      marginBottom: 2,
                    }}
                  >
                    {m.label}
                  </div>
                  <div
                    style={{
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      color: m.color,
                      lineHeight: 1.05,
                    }}
                  >
                    {m.value}
                  </div>
                  {m.meta ? (
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                      {m.meta}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* SG HEALTHCARE / ELDERCARE LINKS */}
      <section className="homeSection">
        <div className="sectionHead">
          <div>
            <div className="sectionTitle">SG Healthcare Services</div>
            <div className="sectionSub">
              Quick links to government services helpful for seniors and
              caregivers
            </div>
          </div>
        </div>

        <div className="svcGrid" style={{ marginTop: 10 }}>
          {SG_CARE_LINKS.map((s) => (
            <a
              key={s.href}
              className="svcCard"
              href={s.href}
              target="_blank"
              rel="noreferrer"
              title={s.title}
            >
              <div className="svcIcon" aria-hidden="true">
                <span className="svcEmoji">{s.emoji || "🏥"}</span>
                {s.img ? (
                  <img
                    className="svcImg"
                    src={s.img}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
              </div>

              <div className="svcBody">
                <div className="svcTitle">{s.title}</div>
                <div className="svcDesc">{s.desc}</div>
              </div>

              <div className="svcArrow" aria-hidden="true">
                →
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="homeSection">
        <div className="sectionHead">
          <div>
            <div className="sectionTitle">
              {q ? "Matching products" : "Featured products"}
            </div>
            <div className="sectionSub">
              {loading ? "Loading…" : `Showing ${featured.length} item(s)`}
            </div>
          </div>

          <Link to="/categories" className="linkBtn">
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="muted homeHint">Loading…</div>
        ) : featured.length === 0 ? (
          <div className="emptyBox">
            <div className="emptyTitle">No items found</div>
            <div className="muted">Try a different search term.</div>
          </div>
        ) : (
          <div className="grid3 homeProductGrid">
            {featured.map((p) => (
              <ProductCard key={p.id} item={p} compact />
            ))}
          </div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="homeSection">
        <div className="sectionHead">
          <div>
            <div className="sectionTitle">Shop</div>
            <div className="sectionSub">Browse by what you need</div>
          </div>

          <Link to="/categories" className="linkBtn">
            Shop →
          </Link>
        </div>

        {loading ? (
          <div className="muted homeHint">Loading…</div>
        ) : (
          <div className="catGrid" style={{ marginTop: 10 }}>
            {categories.map((c, idx) => (
              <CategoryButton
                key={c.id ?? idx}
                cat={c}
                to={`/categories?cat=${c.id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* BUYER REQUESTS */}
      <section className="homeSection">
        <div className="sectionHead">
          <div>
            <div className="sectionTitle">Buyer requests</div>
            <div className="sectionSub">People looking for items right now</div>
          </div>

          <Link to="/requests" className="linkBtn">
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="muted homeHint">Loading…</div>
        ) : buyerRequests.length === 0 ? (
          <div className="emptyBox" style={{ marginTop: 10 }}>
            <div className="emptyTitle">No buyer requests yet</div>
            <div className="muted">Check back later.</div>
          </div>
        ) : (
          <div className="reqGrid" style={{ marginTop: 10 }}>
            {buyerRequests.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                categories={categories}
                compact
                actionTo="/requests"
                actionLabel="Offer an item →"
              />
            ))}
          </div>
        )}
      </section>

      {error ? (
        <div className="muted" style={{ marginTop: 10 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
